import type {
  RegisterToggleElements,
  RegisterToggleMode,
  RegisterToggleNoticeType,
} from './toggle-types';

const HEADING_SELECTOR = '.bde-heading__text, h1, h2';
const SUBMIT_SELECTOR = 'button[type="submit"], .ff_submit_btn_wrapper button, .ff-btn-submit';
const EMAIL_SELECTOR = 'input[type="email"], input[name="email"], input[name*="email"]';
const MODE_SELECTOR = 'input[name="pifster_form_mode"]';
const REGISTER_LINK_TEXT = 'Already a PIFster? Sign in here';

let cachedElements: RegisterToggleElements | null = null;

function requireElement<T extends Element>(root: ParentNode, selector: string, label: string): T {
  const el = root.querySelector<T>(selector);
  if (!el) {
    throw new Error(`[register-toggle] Required element missing: ${label} (${selector})`);
  }
  return el;
}

function findHeading(doc: Document): HTMLElement | null {
  const headings = Array.from(doc.querySelectorAll<HTMLElement>(HEADING_SELECTOR));
  for (const heading of headings) {
    const text = heading.textContent?.trim().toLowerCase() ?? '';
    if (
      text.includes('small change') ||
      text.includes('register') ||
      text.includes('join') ||
      text.includes('welcome back')
    ) {
      return heading;
    }
  }
  return null;
}

function getElementsOrThrow(): RegisterToggleElements {
  if (!cachedElements) {
    throw new Error('[register-toggle] DOM cache has not been initialized.');
  }

  return cachedElements;
}

export function cacheToggleElements(form: HTMLFormElement): RegisterToggleElements {
  if (!(form instanceof HTMLFormElement)) {
    throw new Error('[register-toggle] Expected a form element when caching register toggle DOM.');
  }

  const ownerDocument = form.ownerDocument;
  if (!ownerDocument) {
    throw new Error('[register-toggle] Register toggle form is missing its owner document.');
  }

  const emailFieldEl = requireElement<HTMLInputElement>(form, EMAIL_SELECTOR, 'emailFieldEl');
  const elements: RegisterToggleElements = {
    form,
    headingEl: findHeading(ownerDocument),
    submitButtonEl: form.querySelector<HTMLButtonElement>(SUBMIT_SELECTOR),
    emailFieldEl,
    modeFieldEl: form.querySelector<HTMLInputElement>(MODE_SELECTOR),
    toggleLinkEl: null,
    toggleAnchorEl: null,
    noticeEl: null,
    hiddenActionInputEl: null,
    hiddenNonceInputEl: null,
    hiddenRedirectInputEl: null,
    turnstileContainerEl: null,
    turnstileTokenInputEl: null,
    fluentTurnstileContainerEl: null,
    fluentTurnstileInputEl: null,
    turnstileSource: null,
    originalFormAction: form.action,
  };

  cachedElements = elements;
  return elements;
}

export function createToggleLink(elements: RegisterToggleElements = getElementsOrThrow()): HTMLAnchorElement {
  if (elements.toggleAnchorEl) {
    return elements.toggleAnchorEl;
  }

  const wrapper = elements.form.ownerDocument?.createElement('p');
  const anchor = elements.form.ownerDocument?.createElement('a');
  if (!wrapper || !anchor) {
    throw new Error('[register-toggle] Failed to create toggle link elements.');
  }

  wrapper.className = 'pifster-register-toggle';
  wrapper.style.cssText = 'text-align:center;margin-top:16px;font-size:0.95em;';

  anchor.href = '#';
  anchor.textContent = REGISTER_LINK_TEXT;
  anchor.style.cssText = 'color:#3b82f6;text-decoration:underline;cursor:pointer;';

  wrapper.appendChild(anchor);

  const formParent = elements.form.parentElement;
  if (formParent) {
    formParent.insertAdjacentElement('afterend', wrapper);
  } else {
    elements.form.insertAdjacentElement('afterend', wrapper);
  }

  elements.toggleLinkEl = wrapper;
  elements.toggleAnchorEl = anchor;
  return anchor;
}

export function setHeadingText(elements: RegisterToggleElements = getElementsOrThrow(), text: string): void {
  if (!elements.headingEl) {
    return;
  }

  elements.headingEl.textContent = text;
}

export function setSubmitButtonText(elements: RegisterToggleElements = getElementsOrThrow(), text: string): void {
  if (!elements.submitButtonEl) {
    return;
  }

  elements.submitButtonEl.textContent = text;
}

export function setToggleLinkText(elements: RegisterToggleElements = getElementsOrThrow(), text: string): void {
  if (!elements.toggleAnchorEl) {
    return;
  }

  elements.toggleAnchorEl.textContent = text;
}

export function setLoginNotice(message: string, type: RegisterToggleNoticeType): HTMLParagraphElement {
  const elements = getElementsOrThrow();

  if (!elements.noticeEl) {
    const notice = elements.form.ownerDocument?.createElement('p');
    if (!notice) {
      throw new Error('[register-toggle] Failed to create login notice element.');
    }

    notice.className = 'pifster-login-gate__notice pifster-register-login__notice';
    notice.setAttribute('role', 'alert');
    notice.setAttribute('aria-live', 'polite');

    const parent = elements.form.parentElement;
    if (parent) {
      parent.insertBefore(notice, elements.form);
    } else {
      elements.form.insertAdjacentElement('beforebegin', notice);
    }

    elements.noticeEl = notice;
  }

  elements.noticeEl.textContent = message;
  elements.noticeEl.classList.remove(
    'pifster-register-login__notice--success',
    'pifster-register-login__notice--error',
  );
  elements.noticeEl.classList.add(`pifster-register-login__notice--${type}`);
  return elements.noticeEl;
}

export function setLoginFormInactive(inactive: boolean): void {
  const elements = getElementsOrThrow();
  const controls = elements.form.querySelectorAll<HTMLInputElement | HTMLButtonElement | HTMLSelectElement | HTMLTextAreaElement>(
    'input:not([type="hidden"]), button, select, textarea',
  );

  controls.forEach((control) => {
    control.disabled = inactive;
  });

  elements.form.hidden = inactive;
  elements.form.setAttribute('aria-hidden', inactive ? 'true' : 'false');
  elements.form.setAttribute('aria-busy', inactive ? 'true' : 'false');
}

export function clearLoginNotice(): void {
  const elements = getElementsOrThrow();
  if (!elements.noticeEl) {
    return;
  }

  elements.noticeEl.remove();
  elements.noticeEl = null;
}

export function setRegisterLinkText(text: string): void {
  setToggleLinkText(getElementsOrThrow(), text);
}

export function setModeText(elements: RegisterToggleElements, mode: RegisterToggleMode, registerText: string, loginText: string): void {
  const nextText = mode === 'login' ? loginText : registerText;
  setHeadingText(elements, nextText);
}

export function setSubmitText(elements: RegisterToggleElements, mode: RegisterToggleMode, registerText: string, loginText: string): void {
  const nextText = mode === 'login' ? loginText : registerText;
  setSubmitButtonText(elements, nextText);
}
